import { Invoice } from '../types';
import { openAmountCents, ValidationMessage } from './invoiceLogic';
import { cashDiscountPercent, cashDiscountRemainder, checkCashDiscount } from './cashDiscount';
import { Cents, formatCents } from './money';

/**
 * Was mit dem Rest passiert, wenn der Kunde weniger zahlt, als offen ist.
 *
 * Zwei Arten, beide als eigene Zahlungszeile, die den Rest ausgleicht:
 *   • CASH_DISCOUNT — Skonto (src/services/cashDiscount.ts, SQL Nr. 121–123).
 *   • CUSTOMER_RETENTION — der Kunde behaelt einen Sicherheitseinbehalt ein,
 *     der nicht auf der Rechnung stand. Betreiber-Entscheid 25.09.2026: die
 *     Rechnung gilt als ausgeglichen, der Betrag wird per Trigger (SQL
 *     Nr. 125) als Alt-Einbehalt des Projekts gefuehrt, damit die naechste
 *     Abschlags- oder Schlussrechnung ihn kumulativ beruecksichtigt und am
 *     Ende auszahlt (docs/sicherheitseinbehalt-plan.md, Abschnitt 16).
 * Ohne Wahl bleibt der Rest offen — das ist die Teilzahlung.
 */

export type RemainderKind = 'CASH_DISCOUNT' | 'CUSTOMER_RETENTION';

export const REMAINDER_LABEL: Record<RemainderKind, string> = {
    CASH_DISCOUNT: 'Skonto',
    CUSTOMER_RETENTION: 'Sicherheitseinbehalt',
};

/** Rest, der als Skonto oder Einbehalt in Frage kommt — offen minus Zahlung. */
export const paymentRemainder = cashDiscountRemainder;

/** Anteil am Zahlbetrag der Rechnung in Prozent. */
export const remainderPercent = cashDiscountPercent;

/** Ueblich sind 5–10 % (§ 17 VOB/B); darueber gibt es einen Hinweis. */
const USUAL_RETENTION_MAX_PERCENT = 10;

type InvoiceForRemainder = Pick<
    Invoice,
    'invoiceDate' | 'details' | 'totals' | 'paidCents' | 'creditedCents' | 'primaryProjectId'
>;

const percentText = (value: number): string =>
    value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** Prueft einen vom Kunden einbehaltenen Sicherheitseinbehalt. */
export const checkCustomerRetention = (
    invoice: InvoiceForRemainder,
    retentionCents: Cents,
    alongsideCents: Cents = 0,
): ValidationMessage[] => {
    const out: ValidationMessage[] = [];
    if (!Number.isSafeInteger(retentionCents) || retentionCents <= 0) {
        out.push({
            code: 'retention_invalid',
            level: 'error',
            message: 'Der Sicherheitseinbehalt muss größer als 0,00 € sein.',
        });
        return out;
    }
    const open = openAmountCents(invoice);
    if (alongsideCents + retentionCents > open) {
        out.push({
            code: 'retention_exceeds_open',
            level: 'error',
            message:
                alongsideCents > 0
                    ? `Zahlung und Einbehalt ergeben zusammen ${formatCents(
                          alongsideCents + retentionCents,
                      )}, offen sind nur ${formatCents(open)}.`
                    : `Der Einbehalt (${formatCents(retentionCents)}) ist größer als der offene Betrag (${formatCents(
                          open,
                      )}).`,
        });
        return out;
    }
    // Ohne Projekt kaeme der Einbehalt in keiner Schlussrechnung an — der
    // Trigger aus Nr. 125 bricht dann ab; hier steht es vorher im Klartext.
    if (!invoice.primaryProjectId) {
        out.push({
            code: 'retention_without_project',
            level: 'error',
            message:
                'Die Rechnung gehört zu keinem Projekt. Ein Sicherheitseinbehalt lässt sich nur an einer Projektrechnung führen.',
        });
        return out;
    }
    const onInvoice = invoice.totals.retentionCents ?? 0;
    if (onInvoice > 0) {
        out.push({
            code: 'retention_already_on_invoice',
            level: 'warning',
            message: `Die Rechnung zieht bereits ${formatCents(
                onInvoice,
            )} Sicherheitseinbehalt ab. Bitte prüfen, ob der Kunde doppelt einbehält.`,
        });
    }
    const percent = remainderPercent(invoice, retentionCents);
    if (percent > USUAL_RETENTION_MAX_PERCENT) {
        out.push({
            code: 'retention_unusually_high',
            level: 'warning',
            message: `Der Einbehalt beträgt ${percentText(percent)} % der Rechnung — üblich sind 5–10 %.`,
        });
    }
    return out;
};

/** Prueft den Rest je nach Art. */
export const checkRemainder = (
    invoice: InvoiceForRemainder,
    kind: RemainderKind,
    amountCents: Cents,
    valueDate: string,
    alongsideCents: Cents = 0,
): ValidationMessage[] =>
    kind === 'CASH_DISCOUNT'
        ? checkCashDiscount(invoice, amountCents, valueDate, alongsideCents)
        : checkCustomerRetention(invoice, amountCents, alongsideCents);

/** Ist die Zahlungsart eine Restbuchung (kein Geld, gleicht nur aus)? */
export const isRemainderMethod = (method: string): method is RemainderKind =>
    method === 'CASH_DISCOUNT' || method === 'CUSTOMER_RETENTION';
